import { LoggerInstance } from './LoggerInstance';
import { LogFn, LogLevel } from './types';

/**
 * Logger
 *
 * Wrapper for the LoggerInstance providing us with a singleton without polluting the global namespace
 * https://www.bigbinary.com/blog/singleton-function-in-javascript#single-solution-without-polluting-global-namespace
 */
export const Logger = (() => {
    /** This is our singleton logger instance */
    let instance: LoggerInstance;

    /**
     * getLoggerInstance
     *
     * Will return the existing logger instance or create on if one does not already exist.
     */
    const getLoggerInstance = (
        logLevel: LogLevel = 'debug',
    ): LoggerInstance => {
        if (!instance) {
            instance = LoggerInstance(logLevel);
        }

        return instance;
    };

    return getLoggerInstance;
})();

/* --------------------------- Convenience Methods -------------------------- */
// Methods that can just be imported and used with the least amount of fuss

/**
 * setMinLogLevel
 *
 * Update the Logger instance's minimum log level
 * Any attempts to log using methods below the minLogLevel will be dropped
 *
 * @param minLogLevel
 */
export const setMinLogLevel = (minLogLevel: LogLevel): void => {
    Logger().setMinLogLevel(minLogLevel);
};

/**
 * debug
 *
 * Convenience method to access the debug method on the Logger instance
 */
export const debug: LogFn = (...args): void => Logger().debug(...args);

/**
 * info
 *
 * Convenience method to access the info method on the Logger instance
 */
export const info: LogFn = (...args): void => Logger().info(...args);

/**
 * log
 *
 * Convenience method to access the log method on the Logger instance
 */
export const log: LogFn = (...args): void => Logger().log(...args);

/**
 * warn
 *
 * Convenience method to access the warn method on the Logger instance
 */
export const warn: LogFn = (...args): void => Logger().warn(...args);

/**
 * error
 *
 * Convenience method to access the error method on the Logger instance
 */
export const error: LogFn = (...args): void => Logger().error(...args);
